/**
 * Image Analysis V2 - Redesigned with ConvertMode pattern
 *
 * Features:
 * - Upload or Workspace selection
 * - AI Model selection (if admin allows user_choice)
 * - Tool config from database (ai_service.ai_tools)
 * - Results display with prompts + metadata
 *
 * @accessibility WCAG 2.1 AA + BFSG konform
 */

import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Scan, Sparkles, Image as ImageIcon, Check } from 'lucide-react';
import { Button, Card, CardContent, CardHeader, CardTitle, EmptyState, Loading } from '@/components/v2/ui';
import { InputSourceToggle, UploadArea, type InputSource } from '@/components/v2/tools/shared';
import { ImageGallery } from '@/components/v2/tools/gallery';
import { useWorkspaceImages } from '@/hooks/useWorkspaceImages';
import { analyzeImage } from '@/lib/api/image-analysis';
import { cn } from '@/lib/utils';

type AnalysisState = 'idle' | 'analyzing' | 'done';

interface AnalysisResult {
  id: string;
  system_prompt_generated: string;
  user_prompt_generated: string;
  system_prompt_used: string;
  user_prompt_used: string;
  raw_response: string;
  processing_time_ms: number;
  credits_used: number;
  gemini_model: string;
}

interface AIModel {
  id: string;
  code: string;
  name: string;
  status: string;
}

interface ToolConfig {
  id: string;
  code: string;
  name: string;
  description: string;
  model_selection_mode: 'fixed' | 'user_choice';
  default_model_id: string | null;
  config: {
    temperature: number;
    top_p: number;
    top_k: number;
    max_output_tokens: number;
    system_prompt_template?: string;
    user_prompt_template?: string;
    user_can_edit_system_prompt?: boolean;
    user_can_edit_user_prompt?: boolean;
  };
  ai_models?: AIModel;
  allowed_models?: AIModel[];
}

export function ImageAnalysisV2() {
  const { t } = useTranslation();
  // Input State
  const [inputSource, setInputSource] = useState<InputSource>('upload');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedAssetIds, setSelectedAssetIds] = useState<Set<string>>(new Set());
  const [currentPage, setCurrentPage] = useState(1);

  // Tool Config State
  const [toolConfig, setToolConfig] = useState<ToolConfig | null>(null);
  const [selectedModelId, setSelectedModelId] = useState<string>('');
  const [loadingConfig, setLoadingConfig] = useState(true);

  // Custom Prompts State (user overrides)
  const [customSystemPrompt, setCustomSystemPrompt] = useState<string>('');
  const [customUserPrompt, setCustomUserPrompt] = useState<string>('');

  // Processing State
  const [state, setState] = useState<AnalysisState>('idle');
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState<AnalysisResult | null>(null);

  // Workspace Images Query
  const { images, pagination, isLoading, isFetching } = useWorkspaceImages({
    page: currentPage,
    perPage: 12,
    enabled: inputSource === 'workspace',
  });

  // Load tool config on mount
  useEffect(() => {
    async function loadToolConfig() {
      try {
        const response = await fetch('/api/v1/ai-tool-config/vision_analysis');
        if (!response.ok) throw new Error('Failed to load tool config');

        const config: ToolConfig = await response.json();
        setToolConfig(config);

        // Set default model
        if (config.ai_models) {
          setSelectedModelId(config.ai_models.id);
        }

        // Initialize custom prompts with defaults from config
        setCustomSystemPrompt(config.config.system_prompt_template || '');
        setCustomUserPrompt(config.config.user_prompt_template || '');
      } catch (error) {
        console.error('Failed to load tool config:', error);
        toast.error(t('errors.configLoad'));
      } finally {
        setLoadingConfig(false);
      }
    }

    loadToolConfig();
  }, [t]);

  // Handlers
  const handleSourceChange = (source: InputSource) => {
    setInputSource(source);
    setSelectedFile(null);
    setSelectedAssetIds(new Set());
    setResult(null);
    setState('idle');
  };

  const handleFilesChange = (files: File[]) => {
    if (files.length > 0) {
      setSelectedFile(files[0]);
      setResult(null);
      setState('idle');
    }
  };

  const handleSelectionChange = useCallback((ids: Set<string>) => {
    setSelectedAssetIds(ids);
    setResult(null);
    setState('idle');
  }, []);

  const handleAnalyze = async () => {
    setState('analyzing');
    setProgress(0);
    setResult(null);

    try {
      let fileToAnalyze: File | null = null;

      if (inputSource === 'upload') {
        fileToAnalyze = selectedFile;
      } else {
        // Download workspace image as file
        const selectedId = Array.from(selectedAssetIds)[0];
        if (!selectedId) throw new Error('Kein Bild ausgewählt');

        const selectedImage = images?.find((img) => img.id === selectedId);
        if (!selectedImage) throw new Error('Bild nicht gefunden');

        try {
          const response = await fetch(`/image/${selectedImage.id}`);
          const blob = await response.blob();
          fileToAnalyze = new File([blob], `image_${selectedImage.id}.png`, { type: blob.type });
        } catch {
          throw new Error(`Fehler beim Herunterladen des Bildes`);
        }
      }

      if (!fileToAnalyze) {
        throw new Error('Kein Bild zum Analysieren');
      }

      // Simulate progress
      const progressInterval = setInterval(() => {
        setProgress((prev) => Math.min(prev + Math.random() * 10, 90));
      }, 300);

      // Pass custom prompts + model to API
      const analysisResult = await analyzeImage(fileToAnalyze, {
        system_prompt: customSystemPrompt || undefined,
        user_prompt: customUserPrompt || undefined,
        model_id: selectedModelId || undefined,
      });

      clearInterval(progressInterval);
      setProgress(100);

      setResult(analysisResult);
      setState('done');
      toast.success(t('toast.success.analysisComplete'));
    } catch (error) {
      setState('idle');
      setProgress(0);
      toast.error(error instanceof Error ? error.message : t('toast.error.analysis'));
    }
  };

  const handleReset = () => {
    setResult(null);
    setState('idle');
    setProgress(0);
  };

  const hasInput = inputSource === 'upload' ? selectedFile !== null : selectedAssetIds.size > 0;
  const isAnalyzing = state === 'analyzing';
  const canAnalyze = hasInput && (state === 'idle' || state === 'done') && !loadingConfig;

  const isModelSelectionDisabled =
    toolConfig?.model_selection_mode === 'fixed' || loadingConfig || isAnalyzing;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-3 rounded-xl bg-gradient-to-br from-orange-500 to-orange-600 shadow-lg shadow-orange-500/30">
          <Scan className="h-6 w-6 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-neutral-900 dark:text-white tracking-tight">
            {t('imageAnalysis.analyzeButton')}
          </h1>
          <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">
            {t('imageAnalysis.title')}
          </p>
        </div>
      </div>

      {/* Input View - Always visible */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Input Section */}
        <div className="lg:col-span-2 space-y-4">
          <Card className="rounded-xl shadow-sm border-neutral-200 dark:border-neutral-800">
            <div className="flex items-center gap-3 px-6 py-5 border-b border-neutral-200 dark:border-neutral-800">
              <div className="w-8 h-8 bg-orange-100 dark:bg-orange-900/30 border-2 border-orange-500 rounded-lg flex items-center justify-center font-bold text-sm text-orange-600 dark:text-orange-400">
                1
              </div>
              <h2 className="text-lg font-semibold text-neutral-900 dark:text-white tracking-tight">
                {t('imageAnalysis.selectImage')}
              </h2>
            </div>
            <CardContent className="p-6 space-y-4">
              <InputSourceToggle value={inputSource} onChange={handleSourceChange} />

              {inputSource === 'upload' ? (
                <UploadArea
                  files={selectedFile ? [selectedFile] : []}
                  onFilesChange={handleFilesChange}
                  maxFiles={1}
                  acceptedFormats={['image/png', 'image/jpeg', 'image/webp']}
                  disabled={state === 'analyzing'}
                />
              ) : (
                <>
                  {isLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loading size="lg" />
                    </div>
                  ) : images && images.length > 0 ? (
                    <ImageGallery
                      images={images}
                      isLoading={isLoading}
                      isFetching={isFetching}
                      pagination={pagination}
                      onPageChange={setCurrentPage}
                      selectedIds={selectedAssetIds}
                      onSelectionChange={handleSelectionChange}
                    />
                  ) : (
                    <EmptyState
                      icon={<ImageIcon className="h-12 w-12" />}
                      title="Keine Bilder"
                      description="Es sind noch keine Bilder im Workspace vorhanden."
                    />
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Settings Section */}
        <div className="space-y-4">
          <Card className="rounded-xl shadow-sm border-neutral-200 dark:border-neutral-800">
            <div className="flex items-center gap-3 px-6 py-5 border-b border-neutral-200 dark:border-neutral-800">
              <div className="w-8 h-8 bg-orange-100 dark:bg-orange-900/30 border-2 border-orange-500 rounded-lg flex items-center justify-center font-bold text-sm text-orange-600 dark:text-orange-400">
                2
              </div>
              <h2 className="text-lg font-semibold text-neutral-900 dark:text-white tracking-tight">
                {t('common.settings')}
              </h2>
            </div>
            <CardContent className="p-6 space-y-6">
              {/* Model Selection */}
              {loadingConfig ? (
                <div className="flex items-center justify-center py-4">
                  <Loading size="sm" />
                </div>
              ) : toolConfig ? (
                <div className="space-y-3">
                  <label className="text-sm font-semibold text-neutral-700 dark:text-neutral-200">
                    KI-Modell
                  </label>
                  <select
                    value={selectedModelId}
                    onChange={(e) => setSelectedModelId(e.target.value)}
                    disabled={isModelSelectionDisabled}
                    className={cn(
                      'w-full px-4 py-3 rounded-xl border-2 transition-all',
                      'bg-white dark:bg-neutral-900',
                      'text-neutral-900 dark:text-neutral-100',
                      isModelSelectionDisabled
                        ? 'border-neutral-200 dark:border-neutral-700 cursor-not-allowed opacity-60'
                        : 'border-neutral-300 dark:border-neutral-600 hover:border-orange-400 dark:hover:border-orange-500 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20'
                    )}
                  >
                    {toolConfig.model_selection_mode === 'fixed' && toolConfig.ai_models && (
                      <option value={toolConfig.ai_models.id}>
                        {toolConfig.ai_models.name} (Admin-festgelegt)
                      </option>
                    )}
                    {toolConfig.model_selection_mode === 'user_choice' &&
                      toolConfig.allowed_models?.map((model) => (
                        <option key={model.id} value={model.id}>
                          {model.name}
                        </option>
                      ))}
                  </select>
                  {toolConfig.model_selection_mode === 'fixed' && (
                    <p className="text-xs text-neutral-500 dark:text-neutral-400">
                      Modell wurde vom Administrator festgelegt
                    </p>
                  )}
                </div>
              ) : (
                <p className="text-sm text-red-600 dark:text-red-400">
                  Fehler beim Laden der Konfiguration
                </p>
              )}

              {/* Custom Prompts (if admin allows) */}
              {toolConfig?.config.user_can_edit_system_prompt && (
                <div className="space-y-3">
                  <label className="text-sm font-semibold text-neutral-700 dark:text-neutral-200">
                    System Prompt
                  </label>
                  <textarea
                    value={customSystemPrompt}
                    onChange={(e) => setCustomSystemPrompt(e.target.value)}
                    rows={4}
                    placeholder="System prompt eingeben..."
                    className="w-full px-4 py-3 rounded-xl border-2 border-neutral-300 dark:border-neutral-600 hover:border-orange-400 dark:hover:border-orange-500 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 bg-white dark:bg-neutral-900 text-neutral-900 dark:text-neutral-100 font-mono text-sm transition-all"
                  />
                </div>
              )}

              {toolConfig?.config.user_can_edit_user_prompt && (
                <div className="space-y-3">
                  <label className="text-sm font-semibold text-neutral-700 dark:text-neutral-200">
                    User Prompt
                  </label>
                  <textarea
                    value={customUserPrompt}
                    onChange={(e) => setCustomUserPrompt(e.target.value)}
                    rows={3}
                    placeholder="User prompt eingeben..."
                    className="w-full px-4 py-3 rounded-xl border-2 border-neutral-300 dark:border-neutral-600 hover:border-orange-400 dark:hover:border-orange-500 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 bg-white dark:bg-neutral-900 text-neutral-900 dark:text-neutral-100 font-mono text-sm transition-all"
                  />
                </div>
              )}

              {/* Progress */}
              {state === 'analyzing' && (
                <div className="space-y-3">
                  <div className="flex justify-between text-sm font-medium">
                    <span className="text-neutral-700 dark:text-neutral-200">Analysiere...</span>
                    <span className="text-orange-600 dark:text-orange-400">{Math.round(progress)}%</span>
                  </div>
                  <div className="h-3 bg-neutral-200 dark:bg-neutral-700 rounded-full overflow-hidden shadow-inner">
                    <div
                      className="h-full bg-gradient-to-r from-orange-500 to-orange-600 transition-all duration-300 shadow-lg"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                </div>
              )}

              {/* Analyze Button */}
              <Button
                className="w-full h-12 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 shadow-lg shadow-orange-500/30 hover:shadow-xl hover:shadow-orange-500/40 transition-all duration-200 hover:-translate-y-0.5"
                onClick={handleAnalyze}
                disabled={!canAnalyze || isAnalyzing}
              >
                {isAnalyzing ? (
                  <>
                    <div className="h-5 w-5 mr-2 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    <span className="font-semibold">Analysiere...</span>
                  </>
                ) : (
                  <>
                    <Sparkles className="h-5 w-5 mr-2" />
                    <span className="font-semibold">
                      {hasInput ? t('imageAnalysis.analyzeButton') : t('imageAnalysis.selectImage')}
                    </span>
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Results View - Shown below when done */}
      {state === 'done' && result && (
        <Card className="rounded-xl shadow-sm border-neutral-200 dark:border-neutral-800">
          <CardHeader className="flex flex-row items-center justify-between px-6 py-5 border-b border-neutral-200 dark:border-neutral-800">
            <CardTitle className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-100 dark:bg-green-900/30">
                <Check className="h-5 w-5 text-green-600 dark:text-green-400" />
              </div>
              <span className="text-lg font-semibold text-neutral-900 dark:text-white">
                {t('toast.success.analysisComplete')}
              </span>
            </CardTitle>
            <Button
              onClick={handleReset}
              variant="outline"
              className="hover:bg-orange-50 dark:hover:bg-orange-900/20 hover:border-orange-500"
            >
              Neue Analyse
            </Button>
          </CardHeader>
          <CardContent className="p-6 space-y-6">
            {/* Metadata */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-4 rounded-xl bg-neutral-50 dark:bg-neutral-800/50">
                <p className="text-xs text-neutral-500 dark:text-neutral-400 font-medium mb-1">
                  Modell
                </p>
                <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">
                  {result.gemini_model}
                </p>
              </div>
              <div className="p-4 rounded-xl bg-neutral-50 dark:bg-neutral-800/50">
                <p className="text-xs text-neutral-500 dark:text-neutral-400 font-medium mb-1">
                  Verarbeitungszeit
                </p>
                <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">
                  {(result.processing_time_ms / 1000).toFixed(2)}s
                </p>
              </div>
              <div className="p-4 rounded-xl bg-neutral-50 dark:bg-neutral-800/50">
                <p className="text-xs text-neutral-500 dark:text-neutral-400 font-medium mb-1">
                  Kosten
                </p>
                <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">
                  {result.credits_used.toFixed(2)} Credits
                </p>
              </div>
            </div>

            {/* Raw Response */}
            <div className="space-y-2">
              <h3 className="text-sm font-semibold text-neutral-700 dark:text-neutral-200">
                KI-Antwort (Raw)
              </h3>
              <div className="p-4 rounded-xl bg-neutral-50 dark:bg-neutral-800/50 border border-neutral-200 dark:border-neutral-700">
                <p className="text-sm text-neutral-800 dark:text-neutral-200 whitespace-pre-wrap font-mono">
                  {result.raw_response}
                </p>
              </div>
            </div>

            {/* System Prompt Used */}
            <div className="space-y-2">
              <h3 className="text-sm font-semibold text-neutral-700 dark:text-neutral-200">
                System Prompt (Verwendet)
              </h3>
              <div className="p-4 rounded-xl bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800">
                <p className="text-sm text-neutral-800 dark:text-neutral-200 whitespace-pre-wrap">
                  {result.system_prompt_used}
                </p>
              </div>
            </div>

            {/* User Prompt Used */}
            <div className="space-y-2">
              <h3 className="text-sm font-semibold text-neutral-700 dark:text-neutral-200">
                User Prompt (Verwendet)
              </h3>
              <div className="p-4 rounded-xl bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800">
                <p className="text-sm text-neutral-800 dark:text-neutral-200 whitespace-pre-wrap">
                  {result.user_prompt_used}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default ImageAnalysisV2;
